import React, { useState, useMemo } from 'react';
import { ChevronDown, ChevronUp, Package, ShoppingCart, Sparkles } from 'lucide-react';
import Carousel from '../Carousel';

interface Tier {
  tier_number: number;
  min_quantity: number;
  max_quantity?: number | null;
  discount_percentage?: number | null;
  free_units?: number | null;
}

interface Product {
  id: number;
  consumable_id?: number;
  title: string;
  description: string | null;
  marque: string | null;
  gamme: string | null;
  forme_galenique: string | null;
  conditionnement: string | null;
  unit: string;
  volume_ml?: number | null;
  base_price: number;
  custom_price?: number;
  prix_vente_conseille_ttc?: number | null;
  currency: string;
  batch_size: number | null;
  images?: string[] | null;
  tiers?: Tier[];
  [key: string]: any;
}

interface CategoryGroup {
  key: string;
  label: string;
  products: Product[];
  tiers: Tier[];
}

interface CategoryProductCatalogProps {
  products: Product[];
  onProductClick?: (product: Product) => void;
  onOrderProduct?: (product: Product) => void;
  getPrice: (product: Product) => { price: number; isCustom: boolean; discount: number | null };
  isAdmin?: boolean;
}

const CategoryProductCatalog: React.FC<CategoryProductCatalogProps> = ({
  products,
  onProductClick,
  onOrderProduct,
  getPrice,
  isAdmin: _isAdmin = false,
}) => {
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [selectedTierFilters, setSelectedTierFilters] = useState<Record<string, number | null>>({});

  // Group products by category (marque / gamme / unit)
  const categoryGroups = useMemo(() => {
    const groups: Record<string, CategoryGroup> = {};

    products.forEach((product) => {
      // Build category key from marque + gamme + unit
      const marque = product.marque || 'Sans marque';
      const gamme = product.gamme || 'Sans gamme';
      const unit = product.unit || 'Unité';

      const key = `${marque}|${gamme}|${unit}`;
      const label = `${marque} / ${gamme} / ${unit}`;

      if (!groups[key]) {
        groups[key] = {
          key,
          label,
          products: [],
          tiers: [],
        };
      }

      groups[key].products.push(product);

      // Collect all unique tiers from products in this category
      if (product.tiers && product.tiers.length > 0) {
        product.tiers.forEach((tier) => {
          const existingTier = groups[key].tiers.find(
            (t) => t.min_quantity === tier.min_quantity
          );
          if (!existingTier) {
            groups[key].tiers.push({ ...tier });
          }
        });
      }
    });

    // Sort tiers by min_quantity for each group
    Object.values(groups).forEach((group) => {
      group.tiers.sort((a, b) => a.min_quantity - b.min_quantity);
    });

    return Object.values(groups).sort((a, b) => a.label.localeCompare(b.label));
  }, [products]);

  // Initialize expanded state for all categories
  React.useEffect(() => {
    const allKeys = new Set(categoryGroups.map((g) => g.key));
    setExpandedCategories(allKeys);
  }, [categoryGroups]);

  const toggleCategory = (key: string) => {
    setExpandedCategories((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  const handleTierFilter = (categoryKey: string, minQuantity: number | null) => {
    setSelectedTierFilters((prev) => ({
      ...prev,
      [categoryKey]: prev[categoryKey] === minQuantity ? null : minQuantity,
    }));
  };

  const getTierLabel = (tier: Tier): string => {
    if (tier.discount_percentage && tier.discount_percentage > 0) {
      return `≥${tier.min_quantity} lots -${tier.discount_percentage}%`;
    }
    if (tier.free_units && tier.free_units > 0) {
      return `≥${tier.min_quantity} lots +${tier.free_units} gratuit${tier.free_units > 1 ? 's' : ''}`;
    }
    return `≥${tier.min_quantity} lots`;
  };

  const getNextTierMessage = (group: CategoryGroup): string | null => {
    if (group.tiers.length === 0) return null;
    const firstTier = group.tiers[0];
    if (firstTier.discount_percentage && firstTier.discount_percentage > 0) {
      return `+${firstTier.min_quantity} lots pour Palier 1 (-${firstTier.discount_percentage}%)`;
    }
    if (firstTier.free_units && firstTier.free_units > 0) {
      return `+${firstTier.min_quantity} lots pour Palier 1 (+${firstTier.free_units} gratuit${firstTier.free_units > 1 ? 's' : ''})`;
    }
    return null;
  };

  if (categoryGroups.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-slate-500 dark:text-slate-400">
        <Package className="h-16 w-16 mb-4 opacity-50" />
        <p className="text-lg font-medium">Aucun produit trouvé</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Category count header */}
      <div className="flex justify-end">
        <span className="text-sm text-slate-500 dark:text-slate-400">
          {categoryGroups.length} catégorie{categoryGroups.length > 1 ? 's' : ''}
        </span>
      </div>

      {/* Category groups */}
      {categoryGroups.map((group) => {
        const isExpanded = expandedCategories.has(group.key);
        const selectedTier = selectedTierFilters[group.key];
        const nextTierMessage = getNextTierMessage(group);

        return (
          <div
            key={group.key}
            className="bg-white dark:bg-slate-900 rounded-xl border-2 border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden"
          >
            {/* Category Header */}
            <button
              onClick={() => toggleCategory(group.key)}
              className="w-full flex items-center justify-between px-4 py-3 bg-gradient-to-r from-slate-50 to-blue-50/50 dark:from-slate-800 dark:to-blue-950/20 hover:from-slate-100 hover:to-blue-100/50 dark:hover:from-slate-700 dark:hover:to-blue-900/30 transition-colors"
            >
              <div className="flex items-center gap-3">
                {isExpanded ? (
                  <ChevronUp className="h-5 w-5 text-slate-500" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-slate-500" />
                )}
                <Package className="h-5 w-5 text-cyan-600 dark:text-cyan-400" />
                <span className="font-semibold text-slate-900 dark:text-slate-100">
                  {group.label}
                </span>
              </div>
            </button>

            {/* Expanded content */}
            {isExpanded && (
              <div className="p-4">
                {/* Tier filters and next tier message */}
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                  {/* Tier filter buttons */}
                  <div className="flex flex-wrap gap-2">
                    {group.tiers.map((tier) => {
                      const isSelected = selectedTier === tier.min_quantity;
                      return (
                        <button
                          key={tier.min_quantity}
                          onClick={() => handleTierFilter(group.key, tier.min_quantity)}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${isSelected
                            ? 'bg-cyan-500 text-white shadow-md'
                            : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700'
                            }`}
                        >
                          {getTierLabel(tier)}
                        </button>
                      );
                    })}
                  </div>

                  {/* Next tier message */}
                  {nextTierMessage && (
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-full">
                      <Sparkles className="h-4 w-4 text-amber-500" />
                      <span className="text-xs font-medium text-amber-700 dark:text-amber-400">
                        {nextTierMessage}
                      </span>
                    </div>
                  )}
                </div>

                {/* Products grid */}
                <div className="flex flex-wrap gap-3">
                  {group.products
                    .filter((item) => {
                      // If no tier filter selected, show all products
                      if (!selectedTier) return true;
                      // Show only products that have a tier matching the selected min_quantity
                      return item.tiers?.some((t) => t.min_quantity === selectedTier);
                    })
                    .map((item) => {
                      const priceInfo = getPrice(item);
                      return (
                        <div
                          key={item.id}
                          onClick={() => onProductClick?.(item)}
                          className="group w-full sm:w-[360px] p-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:shadow-lg hover:border-cyan-300 dark:hover:border-cyan-700 cursor-pointer transition-all duration-200"
                        >
                          <div className="flex items-start gap-3">
                            {/* Image Carousel */}
                            <div className="w-16 h-16 bg-slate-100 dark:bg-slate-700 rounded-lg overflow-hidden flex-shrink-0">
                              <Carousel
                                images={item.images || []}
                                alt={item.title}
                                className="w-full h-full"
                                showDots={false}
                                showArrows={false}
                                autoPlay={false}
                                compact={true}
                              />
                            </div>

                            {/* Product Info */}
                            <div className="min-w-0 flex-1">
                              <h3 className="font-bold text-slate-900 dark:text-slate-100 mb-1 text-sm truncate">
                                {item.title}
                              </h3>
                              <p className="text-xs text-slate-600 dark:text-slate-400 line-clamp-1">
                                {item.description || 'Aucune description'}
                              </p>
                            </div>

                            {/* Order Button */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onOrderProduct?.(item);
                              }}
                              className="shrink-0 flex items-center justify-center gap-2 px-3 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white rounded-lg shadow-md hover:shadow-lg hover:scale-[1.02] active:scale-95 transition-all duration-200 text-sm font-medium"
                            >
                              <ShoppingCart className="h-4 w-4" />
                              Commander
                            </button>
                          </div>

                          <div className="mt-2 space-y-1">
                            {/* Prix Catalogue */}
                            <div className="flex items-center gap-1 text-xs text-slate-600 dark:text-slate-400 whitespace-nowrap">
                              <span>Prix Catalogue :</span>
                              <span className="font-bold text-cyan-600 dark:text-cyan-400">
                                {priceInfo.price.toFixed(2)} € HT
                              </span>
                              <span className="text-slate-500">/ {item.unit}</span>
                            </div>

                            {/* Prix conseillé */}
                            {item.prix_vente_conseille_ttc != null && (
                              <div className="flex items-center gap-1 text-xs text-slate-600 dark:text-slate-400 whitespace-nowrap">
                                <span>Prix de vente conseillé :</span>
                                <span className="font-bold text-cyan-600 dark:text-cyan-400">
                                  {Number(item.prix_vente_conseille_ttc).toFixed(2)} € TTC
                                </span>
                                <span className="text-slate-500">/ {item.unit}</span>
                              </div>
                            )}

                            {/* Minimum de commande */}
                            <p className="text-xs text-slate-600 dark:text-slate-400">
                              Minimum de commande :{' '}
                              <span className="font-medium">
                                Lot de {item.batch_size || 1} {item.unit}
                                {/* {(item.batch_size || 1) > 1 ? 's' : ''} */}
                              </span>
                            </p>

                            {/* Remise info */}
                            {item.tiers && item.tiers.length > 0 && (() => {
                              const firstTier = [...item.tiers].sort(
                                (a, b) => a.min_quantity - b.min_quantity
                              )[0];
                              return (
                                <p className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">
                                  Remise à partir de {firstTier.min_quantity} lot
                                  {firstTier.min_quantity > 1 ? 's' : ''}
                                </p>
                              );
                            })()}
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default CategoryProductCatalog;
